// src/pages/SearchPage.js
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { searchByTag, updateCattle } from '../utils/cattleService';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const EXIT_REASONS = ['DEATH', 'SALE', 'DONATE'];

export default function SearchPage() {
  const { currentUser, canEdit, incrementEditCount } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [exitDate, setExitDate] = useState('');
  const [exitReason, setExitReason] = useState('');
  const navigate = useNavigate();

  async function handleSearch(e) {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    const res = await searchByTag(currentUser.uid, query.trim());
    setResults(res);
    setSearched(true);
    setLoading(false);
    if (res.length === 0) toast('No cattle found with tag: ' + query);
  }

  function startEdit(record) {
    if (!canEdit) { toast.error('Edit limit reached. Upgrade to Pro!'); navigate('/upgrade'); return; }
    setEditingId(record.id);
    setExitDate(record.dateOfExit || '');
    setExitReason(record.exitReason || '');
  }

  async function saveExit(id) {
    if (!exitDate) { toast.error('Please enter Date of Exit'); return; }
    if (!exitReason) { toast.error('Please select a reason'); return; }
    await updateCattle(currentUser.uid, id, { dateOfExit: exitDate, exitReason });
    await incrementEditCount();
    toast.success('Exit details saved!');
    setEditingId(null);
    // refresh results
    const res = await searchByTag(currentUser.uid, query.trim());
    setResults(res);
  }

  return (
    <div className="page-container">
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1b5e20', marginBottom: 6 }}>🔍 Search by Tag Number</h1>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>Enter a tag number to find matching records and update exit details.</p>

      <div className="card" style={{ marginBottom: 24 }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10 }}>
          <div className="search-box" style={{ flex: 1 }}>
            <span className="icon">🏷️</span>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Enter Tag Number (e.g. 102347020244)"
              style={{ paddingLeft: 36 }}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Searching...' : '🔍 Search'}
          </button>
        </form>
      </div>

      {loading && <div className="loading"><div className="spinner"></div></div>}

      {searched && !loading && results.length === 0 && (
        <div className="empty-state">
          <div style={{ fontSize: 48 }}>🔎</div>
          <p>No cattle found with tag number: <strong>{query}</strong></p>
        </div>
      )}

      {results.map(r => (
        <div key={r.id} className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1b5e20' }}>🏷️ {r.tagNumber}</h2>
                <span className={`badge ${r.dateOfExit ? 'badge-exited' : 'badge-alive'}`}>
                  {r.dateOfExit ? '🚪 Exited' : '✅ Alive'}
                </span>
                <span className={`badge ${r.gender === 'MALE' ? 'badge-male' : 'badge-female'}`}>
                  {r.gender}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                {[
                  ['📅 Entry Date', r.dateOfEntry],
                  ['🐄 Breed', r.breed],
                  ['📏 Age', `${r.age} years`],
                  ['🎨 Color', r.color],
                  ['🏷️ Tag Color', r.tagColor],
                  ['🔖 Identifier', r.identifier],
                  ['⚕️ Disability', r.disability || 'None'],
                ].map(([label, val]) => (
                  <div key={label} style={{ background: '#f9fafb', borderRadius: 7, padding: '8px 12px' }}>
                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{val || '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {editingId === r.id ? (
            <div style={{ marginTop: 16, background: '#fefce8', border: '1px solid #fde047', borderRadius: 10, padding: 16 }}>
              <h4 style={{ fontWeight: 700, marginBottom: 12, color: '#92400e' }}>📝 Update Exit Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>Date of Exit (निकास की दिनांक) *</label>
                  <input type="text" value={exitDate} onChange={e => setExitDate(e.target.value)} placeholder="DD/MM/YY" />
                </div>
                <div className="form-group">
                  <label>Reason (कारण) *</label>
                  <select value={exitReason} onChange={e => setExitReason(e.target.value)}>
                    <option value="">Select reason...</option>
                    {EXIT_REASONS.map(reason => <option key={reason}>{reason}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" onClick={() => saveExit(r.id)}>💾 Save Exit</button>
                <button className="btn btn-secondary" onClick={() => setEditingId(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 14, display: 'flex', gap: 10, borderTop: '1px solid #f0f0f0', paddingTop: 14 }}>
              {r.dateOfExit ? (
                <div style={{ flex: 1, display: 'flex', gap: 20 }}>
                  <span style={{ color: '#6b7280', fontSize: 13 }}>Exit Date: <strong>{r.dateOfExit}</strong></span>
                  <span style={{ color: '#6b7280', fontSize: 13 }}>Reason: <strong>{r.exitReason}</strong></span>
                </div>
              ) : (
                <span style={{ color: '#6b7280', fontSize: 13 }}>No exit recorded — cattle is alive</span>
              )}
              <button className="btn btn-amber" onClick={() => startEdit(r)}>
                {r.dateOfExit ? '✏️ Update Exit' : '🚪 Record Exit'}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
